import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface OptimizedAuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  loading: boolean;
  sessionReady: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isSuperAdmin: boolean;
  hasAdminAccess: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
  signUp: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
  refreshRole: () => Promise<void>;
}

const OptimizedAuthContext = createContext<OptimizedAuthContextType | undefined>(undefined);

export const OptimizedAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);

  const refreshRole = async () => {
    // Get current session and user directly from Supabase to ensure we have the latest state
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const currentUser = currentSession?.user;

    if (!currentUser?.id) {
      
      setUserRole(null);
      return;
    }

    try {
      
      
      // Try using the database function first
      const { data: roleData, error: roleError } = await supabase.rpc('get_user_role');
      
      if (!roleError && roleData) {
        
        setUserRole(roleData);
        return;
      }

      

      // Fallback to direct query with .maybeSingle() to handle missing profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) {
        // For superadmin test account, create profile if missing
        if (currentUser.email === 'superadmin@vramstore.com') {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: currentUser.id,
              email: currentUser.email,
              role: 'super_admin',
              full_name: 'Super Administrator'
            });
          
          if (!insertError) {
            setUserRole('super_admin');
            return;
          }
        }
        setUserRole('customer');
      } else {
        const role = data?.role || 'customer';
        
        setUserRole(role);
      }
    } catch (error) {
      
      setUserRole('customer');
    }
  };

  useEffect(() => {
    
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Enhanced session readiness check
        const isSessionReady = !!(
          session?.access_token && 
          session?.user && 
          session.expires_at && 
          Date.now() < session.expires_at * 1000
        );
        setSessionReady(isSessionReady);
        
        if (session?.user) {
          // Fetch role after session is ready
          if (isSessionReady) {
            setTimeout(() => refreshRole(), 100);
          }
        } else {
          // User signed out, clear everything
          
          setUserRole(null);
          setSessionReady(false);
        }
        
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      
      
      setSession(session);
      setUser(session?.user ?? null);
      
      const isSessionReady = !!(
        session?.access_token && 
        session?.user && 
        session.expires_at && 
        Date.now() < session.expires_at * 1000
      );
      setSessionReady(isSessionReady);
      
      if (session?.user && isSessionReady) {
        // Fetch role after setup
        setTimeout(() => refreshRole(), 100);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = "/auth";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      return { data, error };
    } catch (error) {
      return { error };
    }
  };

  const memoizedValue = useMemo(() => {
    const isAdmin = userRole === 'admin';
    const isManager = userRole === 'manager';
    const isSuperAdmin = userRole === 'super_admin';
    const hasAdminAccess = isAdmin || isManager || isSuperAdmin;


    return {
      user,
      session,
      userRole,
      loading,
      sessionReady,
      isAdmin,
      isManager,
      isSuperAdmin,
      hasAdminAccess,
      signOut,
      signIn,
      signUp,
      refreshRole
    };
  }, [user, session, userRole, loading, sessionReady]);

  return (
    <OptimizedAuthContext.Provider value={memoizedValue}>
      {children}
    </OptimizedAuthContext.Provider>
  );
};

export const useOptimizedAuth = () => {
  const context = useContext(OptimizedAuthContext);
  if (context === undefined) {
    throw new Error("useOptimizedAuth must be used within an OptimizedAuthProvider");
  }
  return context;
};